import Keycloak from 'keycloak-js';
import { KEYCLOAK_CONFIG } from '../config/env';

export const keycloak = new Keycloak({
  url: KEYCLOAK_CONFIG.url,
  realm: KEYCLOAK_CONFIG.realm,
  clientId: KEYCLOAK_CONFIG.clientId,
});

let _initPromise: Promise<boolean> | null = null;

export const initKeycloak = (): Promise<boolean> => {
  if (_initPromise) return _initPromise;

  _initPromise = keycloak
    .init({
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
      silentCheckSsoFallback: false,
      pkceMethod: 'S256',
      checkLoginIframe: false,
    })
    .then((authenticated) => {
      if (authenticated && window.location.search.includes('state=')) {
        const url = new URL(window.location.href);
        url.searchParams.delete('state');
        url.searchParams.delete('session_state');
        url.searchParams.delete('iss');
        url.searchParams.delete('code');
        window.history.replaceState({}, document.title, url.pathname + url.search);
      }
      return authenticated;
    })
    .catch((err) => {
      if (err instanceof Error && err.message.includes('initialized once')) {
        return keycloak.authenticated ?? false;
      }
      _initPromise = null;
      return false;
    });

  return _initPromise;
};

export const login = (redirectPath?: string) =>
  keycloak.login({
    redirectUri: redirectPath
      ? window.location.origin + redirectPath
      : window.location.origin + '/dashboard',
  });

export const logout = () =>
  keycloak.logout({ redirectUri: window.location.origin });

export const getToken = () => keycloak.token;

export const getUserInfo = () => {
  const p = keycloak.tokenParsed;
  if (!p) return null;

  const realmRoles: string[] = p.realm_access?.roles ?? [];
  const resourceRoles: string[] = [];
  if (p.resource_access) {
    for (const resource of Object.values(p.resource_access)) {
      const r = resource as { roles?: string[] };
      if (r.roles) resourceRoles.push(...r.roles);
    }
  }

  return {
    id: p.sub ?? '',
    username: (p.preferred_username as string) ?? '',
    email: (p.email as string) ?? '',
    firstName: (p.given_name as string) ?? '',
    lastName: (p.family_name as string) ?? '',
    roles: [...new Set([...realmRoles, ...resourceRoles])],
  };
};

// Role-based authorization disabled for now — a separate authz system is
// planned. Any authenticated user passes every role check until that lands.
export const hasRole = (_role: string): boolean => true;

export const hasAnyRole = (roles: string[]): boolean => roles.some(hasRole);
export const hasAllRoles = (roles: string[]): boolean => roles.every(hasRole);

// Returns orgId from JWT claim 'org_id' / 'tenant_id', falling back to VITE_ORG_ID env var
export const getOrgId = (): string => {
  const p = keycloak.tokenParsed as Record<string, any> | undefined;
  return p?.org_id ?? p?.tenant_id ?? import.meta.env.VITE_ORG_ID ?? '';
};
